import { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  ArrowLeft,
  Plus,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Minimize2,
  X,
  Mic,
  Upload,
  Compass,
  FlaskConical,
  Search,
  BookOpen,
  Wrench,
  Puzzle,
  LayoutGrid,
  Play,
  Flag,
  Brain,
  Bot,
  MoreHorizontal,
  PlusCircle,
  Clipboard,
  MousePointer2,
  Hand,
  Grid3X3,
  LayoutDashboard,
  Trash2,
  PlayCircle,
} from "lucide-react";

export type MedicalBotNode = {
  component_id: string;
  component_type: string;
  component_name: string;
  component_status?: string;
  input_type?: string[];
  models?: { text?: string[] };
  next_component_id?: string[];
  agent_info?: string;
  system_prompt?: string;
  user_prompt?: string;
};

const MEDICAL_WORKFLOW_NODES: MedicalBotNode[] = [
  {
    component_id: "id_start",
    component_type: "start",
    component_name: "Start",
    input_type: ["text", "audio"],
    models: { text: ["openai/gpt-oss-120b", "llama-3"] },
    next_component_id: ["id_master_agent"],
  },
  {
    component_id: "id_master_agent",
    component_type: "agent",
    component_name: "Master Agent",
    next_component_id: [
      "id_patient_management_agent",
      "id_appointment_scheduling_agent",
      "id_medical_record_agent",
      "id_prescription_lab_agent",
    ],
  },
  {
    component_id: "id_patient_management_agent",
    component_type: "agent",
    component_name: "patient_management_agent",
    next_component_id: ["id_end"],
  },
  {
    component_id: "id_appointment_scheduling_agent",
    component_type: "agent",
    component_name: "appointment_scheduling_agent",
    next_component_id: ["id_end"],
  },
  {
    component_id: "id_medical_record_agent",
    component_type: "agent",
    component_name: "medical_record_agent",
    next_component_id: ["id_end"],
  },
  {
    component_id: "id_prescription_lab_agent",
    component_type: "agent",
    component_name: "prescription_lab_agent",
    next_component_id: ["id_end"],
  },
  {
    component_id: "id_end",
    component_type: "end",
    component_name: "End",
    next_component_id: [],
  },
];

function WorkflowNodeCard({
  node,
  getNodeIcon,
  getNodeIconBg,
  getModelName,
  isSelected,
  onOpenEdit,
}: {
  node: MedicalBotNode;
  getNodeIcon: (n: MedicalBotNode) => typeof Play;
  getNodeIconBg: (type: string) => string;
  getModelName: (n: MedicalBotNode) => string;
  isSelected?: boolean;
  onOpenEdit?: (node: MedicalBotNode) => void;
}) {
  const Icon = getNodeIcon(node);
  const type = node.component_type?.toLowerCase();
  return (
    <div
      className={`w-[220px] bg-white rounded border-4 transition-all duration-200 cursor-default relative hover:shadow-lg hover:-translate-y-0.5 ${isSelected ? "border-indigo-500 shadow-md" : "border-gray-200 hover:border-gray-300"}`}
    >
      <div className="absolute left-[-6px] top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-white border-2 border-gray-300 rounded-full" />
      <div className="absolute right-[-6px] top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-white border-2 border-gray-300 rounded-full" />
      <div className="px-4 py-4 flex items-center gap-3 border-b border-gray-100">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${getNodeIconBg(type)}`}>
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-gray-900 truncate text-lg">{node.component_name}</div>
          <div className="text-xs font-bold text-gray-500 uppercase tracking-wide">{node.component_type}</div>
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpenEdit?.(node); }}
          className="p-1 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          title="Open config"
        >
          <MoreHorizontal size={16} />
        </button>
      </div>
      <div className="px-4 py-3 min-h-[40px] flex items-center">
        {type === "agent" && (
          <div className="flex items-center gap-2 bg-gray-200/80 px-2 py-1 rounded-md text-sm text-gray-700">
            <span>🤖</span>
            <span className="truncate">{getModelName(node)}</span>
          </div>
        )}
        {type === "start" && (
          <span className="text-xs text-gray-500 italic">Click to configure</span>
        )}
      </div>
    </div>
  );
}

type Props = {
  onBack: () => void;
};

export default function MedicalBotStudio({ onBack }: Props) {
  const [nodes, setNodes] = useState<MedicalBotNode[]>(() => [...MEDICAL_WORKFLOW_NODES]);
  const [stagingNewAgent, setStagingNewAgent] = useState<MedicalBotNode | null>(null);
  const [selectedNode, setSelectedNode] = useState<MedicalBotNode | null>(null);
  const [rightPanelVisible, setRightPanelVisible] = useState(false);
  const [activeCanvasTool, setActiveCanvasTool] = useState<number>(2); // 2 = Select (default)
  const [zoom, setZoom] = useState(0.85);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isFittedView, setIsFittedView] = useState(false);
  const [nodePositions, setNodePositions] = useState<Record<string, { x: number; y: number }>>({});
  const [draggingNode, setDraggingNode] = useState<string | null>(null);
  const [isPanning, setIsPanning] = useState(false);
  const [svgLines, setSvgLines] = useState<{ id: string; d: string; markerEnd: string; markerStart?: string; stroke: string; strokeWidth: number }[]>([]);
  const [inputType, setInputType] = useState<"text" | "audio">("text");
  const [inputText, setInputText] = useState("");
  const [selectedModel, setSelectedModel] = useState("");
  const [newAgentName, setNewAgentName] = useState("");
  const canvasRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef({ mouseX: 0, mouseY: 0, nodeX: 0, nodeY: 0 });
  const panStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const isDraggingNodeRef = useRef(false);
  const savedZoomPanRef = useRef<{ zoom: number; pan: { x: number; y: number } }>({ zoom: 0.85, pan: { x: 0, y: 0 } });

  const getNode = useCallback(
    (id: string) => nodes.find((n) => n.component_id === id),
    [nodes],
  );

  const handleZoomIn = () => { setIsFittedView(false); setZoom((z) => Math.min(z + 0.1, 2)); };
  const handleZoomOut = () => { setIsFittedView(false); setZoom((z) => Math.max(z - 0.1, 0.4)); };
  const handleFit = () => {
    if (isFittedView) {
      setZoom(savedZoomPanRef.current.zoom);
      setPan({ ...savedZoomPanRef.current.pan });
      setIsFittedView(false);
      return;
    }
    if (!contentRef.current || !canvasRef.current) {
      setZoom(1);
      setPan({ x: 0, y: 0 });
      return;
    }
    savedZoomPanRef.current = { zoom, pan: { ...pan } };
    const contentWidth = contentRef.current.offsetWidth;
    const contentHeight = contentRef.current.offsetHeight;
    const canvasRect = canvasRef.current.getBoundingClientRect();
    if (contentWidth <= 0 || contentHeight <= 0 || canvasRect.width <= 0 || canvasRect.height <= 0) {
      setZoom(1);
      setPan({ x: 0, y: 0 });
      return;
    }
    const padding = 0.9;
    const fitZoom = Math.min(
      (canvasRect.width / contentWidth) * padding,
      (canvasRect.height / contentHeight) * padding,
    );
    const clampedZoom = Math.min(2, Math.max(0.4, fitZoom));
    const panX = (canvasRect.width - contentWidth * clampedZoom) / 2;
    const panY = (canvasRect.height - contentHeight * clampedZoom) / 2;
    setZoom(clampedZoom);
    setPan({ x: panX, y: panY });
    setIsFittedView(true);
  };

  const addAgent = () => {
    setSelectedNode(null);
    const newId = `id_agent_${Date.now()}`;
    const newNode: MedicalBotNode = {
      component_id: newId,
      component_type: "agent",
      component_name: "New Agent",
      next_component_id: ["id_end"],
    };
    setStagingNewAgent(newNode);
    setNewAgentName("New Agent");
  };

  const handleNodeClick = (node: MedicalBotNode) => {
    if (isDraggingNodeRef.current) return;
    setStagingNewAgent(null);
    setSelectedNode(node);
  };

  const updateNode = useCallback((id: string, updates: Partial<MedicalBotNode>) => {
    setNodes((prev) =>
      prev.map((n) => (n.component_id === id ? { ...n, ...updates } : n)),
    );
    if (selectedNode?.component_id === id) {
      setSelectedNode((prev) => (prev ? { ...prev, ...updates } : null));
    }
  }, [selectedNode?.component_id]);

  const CORE_NODE_IDS = ["id_start", "id_master_agent", "id_end"];
  const deleteNode = useCallback((id: string) => {
    if (CORE_NODE_IDS.includes(id)) return;
    setNodes((prev) => {
      const filtered = prev.filter((n) => n.component_id !== id);
      return filtered.map((n) => ({
        ...n,
        next_component_id: n.next_component_id?.filter((nextId) => nextId !== id) || [],
      }));
    });
    if (selectedNode?.component_id === id) handleCloseRightPanel();
  }, [selectedNode?.component_id]);

  // Unified right panel: open for Add or Edit, close with animation
  const handleCloseRightPanel = useCallback(() => {
    setRightPanelVisible(false);
    setTimeout(() => {
      setStagingNewAgent(null);
      setSelectedNode(null);
      setNewAgentName("");
      setInputType("text");
      setInputText("");
      setSelectedModel("");
    }, 520);
  }, []);

  useEffect(() => {
    if (stagingNewAgent || selectedNode) {
      const t = setTimeout(() => setRightPanelVisible(true), 20);
      return () => clearTimeout(t);
    } else {
      setRightPanelVisible(false);
    }
  }, [stagingNewAgent, selectedNode]);

  const confirmAddAgent = () => {
    if (!stagingNewAgent) return;
    const newNode: MedicalBotNode = {
      ...stagingNewAgent,
      component_name: newAgentName || stagingNewAgent.component_name,
      input_type: [inputType],
      models: selectedModel ? { text: [selectedModel] } : undefined,
    };
    setNodes((prev) => [...prev, newNode]);
    handleCloseRightPanel();
  };

  const getNodeIcon = (node: MedicalBotNode) => {
    const type = node.component_type?.toLowerCase();
    const name = node.component_name?.toLowerCase();
    if (type === "start") return Play;
    if (type === "end") return Flag;
    if (name === "master agent") return Brain;
    return Bot;
  };

  const getNodeIconBg = (type: string) => {
    if (type === "start") return "bg-blue-100 text-blue-600";
    if (type === "end") return "bg-emerald-100 text-emerald-600";
    return "bg-violet-100 text-violet-600";
  };

  const getModelName = (node: MedicalBotNode) =>
    node.models?.text?.[0] || "gpt-4o-mini";

  // Connectors: bidirectional support + completed (green) style (ocr-ui style)
  useEffect(() => {
    if (!contentRef.current || nodes.length === 0) return;
    const getNodeById = (id: string) => nodes.find((n) => n.component_id === id);
    const calculateLines = () => {
      const contentRect = contentRef.current!.getBoundingClientRect();
      const wrappers = contentRef.current!.querySelectorAll<HTMLElement>("[data-node-id]");
      const nodeMap: Record<string, { x: number; y: number; width: number; height: number }> = {};
      wrappers.forEach((el) => {
        const id = el.getAttribute("data-node-id");
        if (!id) return;
        const rect = el.getBoundingClientRect();
        nodeMap[id] = {
          x: (rect.left - contentRect.left) / zoom,
          y: (rect.top - contentRect.top) / zoom,
          width: rect.width / zoom,
          height: rect.height / zoom,
        };
      });
      type LineEntry = { id: string; d: string; markerEnd: string; markerStart?: string; stroke: string; strokeWidth: number };
      const lines: LineEntry[] = [];
      const greyStroke = "#94a3b8";
      const greenStroke = "#34a853";

      nodes.forEach((node) => {
        const nextIds = node.next_component_id || [];
        const start = nodeMap[node.component_id];
        if (!start) return;
        const startX = start.x + start.width;
        const startY = start.y + start.height / 2;

        nextIds.forEach((nextId) => {
          const end = nodeMap[nextId];
          if (!end) return;
          const endNode = getNodeById(nextId);
          const isCompleted =
            node.component_status === "completed" && (endNode?.component_status === "completed");

          const reverseExists = (getNodeById(nextId)?.next_component_id ?? []).includes(node.component_id);

          if (reverseExists) {
            // Bidirectional: draw once (when id < nextId to avoid duplicate)
            if (node.component_id >= nextId) return;
            const leftNode = start.x < end.x ? start : end;
            const rightNode = start.x < end.x ? end : start;
            const leftId = start.x < end.x ? node.component_id : nextId;
            const rightId = start.x < end.x ? nextId : node.component_id;
            const lineStartX = leftNode.x + leftNode.width;
            const lineStartY = leftNode.y + leftNode.height / 2;
            const lineEndX = rightNode.x;
            const lineEndY = rightNode.y + rightNode.height / 2;
            const cp1X = lineStartX + (lineEndX - lineStartX) / 2;
            const cp2X = lineStartX + (lineEndX - lineStartX) / 2;
            lines.push({
              id: `${leftId}-${rightId}-bidir`,
              d: `M ${lineStartX} ${lineStartY} C ${cp1X} ${lineStartY}, ${cp2X} ${lineEndY}, ${lineEndX} ${lineEndY}`,
              markerEnd: isCompleted ? "url(#arrowhead-completed)" : "url(#arrowhead)",
              markerStart: isCompleted ? "url(#arrowhead-start-completed)" : "url(#arrowhead-start)",
              stroke: isCompleted ? greenStroke : greyStroke,
              strokeWidth: isCompleted ? 2 : 1.5,
            });
          } else {
            // Unidirectional
            const endX = end.x;
            const endY = end.y + end.height / 2;
            const cp1X = startX + (endX - startX) / 2;
            const cp2X = startX + (endX - startX) / 2;
            lines.push({
              id: `${node.component_id}-${nextId}`,
              d: `M ${startX} ${startY} C ${cp1X} ${startY}, ${cp2X} ${endY}, ${endX} ${endY}`,
              markerEnd: isCompleted ? "url(#arrowhead-completed)" : "url(#arrowhead)",
              stroke: isCompleted ? greenStroke : greyStroke,
              strokeWidth: isCompleted ? 2 : 1.5,
            });
          }
        });
      });
      setSvgLines(lines);
    };
    const t = setTimeout(calculateLines, 50);
    window.addEventListener("resize", calculateLines);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", calculateLines);
    };
  }, [nodes, zoom, nodePositions, pan]);

  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    if (draggingNode) return;
    if ((e.target as HTMLElement).closest("[data-node-id]")) return;
    setIsFittedView(false);
    setIsPanning(true);
    panStartRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (draggingNode) {
        isDraggingNodeRef.current = true;
        const dx = (e.clientX - dragStartRef.current.mouseX) / zoom;
        const dy = (e.clientY - dragStartRef.current.mouseY) / zoom;
        setNodePositions((prev) => ({
          ...prev,
          [draggingNode]: {
            x: dragStartRef.current.nodeX + dx,
            y: dragStartRef.current.nodeY + dy,
          },
        }));
      } else if (isPanning) {
        setPan({
          x: panStartRef.current.panX + (e.clientX - panStartRef.current.x),
          y: panStartRef.current.panY + (e.clientY - panStartRef.current.y),
        });
      }
    },
    [draggingNode, isPanning, zoom],
  );

  const handleMouseUp = useCallback(() => {
    setDraggingNode(null);
    setIsPanning(false);
    isDraggingNodeRef.current = false;
  }, []);

  useEffect(() => {
    if (!draggingNode && !isPanning) return;
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [draggingNode, isPanning, handleMouseMove, handleMouseUp]);

  const handleNodeMouseDown = (e: React.MouseEvent, nodeId: string) => {
    e.stopPropagation();
    // Pan mode (index 3): clicking on a node starts canvas pan instead of node drag
    if (activeCanvasTool === 3) {
      setIsFittedView(false);
      setIsPanning(true);
      panStartRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
      return;
    }
    // Select mode (index 2) or other: node drag
    const pos = nodePositions[nodeId] || { x: 0, y: 0 };
    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      nodeX: pos.x,
      nodeY: pos.y,
    };
    setDraggingNode(nodeId);
  };

  const startNode = getNode("id_start");
  const masterNode = getNode("id_master_agent");
  const agentNodes = nodes.filter(
    (n) =>
      n.component_type === "agent" &&
      !["id_master_agent"].includes(n.component_id),
  );
  const endNode = getNode("id_end");

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-100">
      {/* Top bar: Back + Add Agent only (zoom is on canvas) */}
      <div className="flex items-center justify-between gap-4 px-4 py-2 bg-white border-b border-gray-200 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg shrink-0"
          >
            <ArrowLeft size={18} />
            Back to Agentic AI
          </button>
          <span className="text-gray-400 shrink-0">|</span>
          <span className="text-sm font-semibold text-gray-800 truncate">
            Medical Bot – Studio
          </span>
        </div>
        <button
          onClick={addAgent}
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 shrink-0"
        >
          <Plus size={16} />
          Add Agent
        </button>
      </div>

      {/* Sub-header: Explore, Studio, DeepResearch… – full width so white background goes full left */}
      <div className="shrink-0 flex flex-row justify-center items-center gap-1 py-2 px-4 bg-white border-b border-gray-200">
        {[
          { id: "explore", label: "Explore", icon: Compass },
          { id: "studio", label: "Studio", icon: FlaskConical },
          { id: "deepresearch", label: "DeepResearch", icon: Search },
          { id: "knowledge", label: "Knowledge", icon: BookOpen },
          { id: "tools", label: "Tools", icon: Wrench },
          { id: "plugins", label: "Plugins", icon: Puzzle },
          { id: "more", label: "More", icon: LayoutGrid },
        ].map(({ id, label, icon: Icon }) => {
          const isStudio = id === "studio";
          return (
            <div
              key={id}
              title={label}
              className={`w-10 h-10 flex items-center justify-center rounded-lg cursor-default ${
                isStudio
                  ? "bg-indigo-100 text-indigo-600"
                  : "text-gray-400 hover:bg-gray-50 hover:text-gray-600"
              }`}
            >
              <Icon size={20} />
            </div>
          );
        })}
      </div>

      <div className="flex-1 flex flex-row flex-nowrap overflow-hidden min-h-0">
        {/* Vertical canvas tools toolbar (ocr-ui style: Add, Paste, Select, Pan, Grid, Minimap, More) */}
        <div className="w-[60px] shrink-0 flex flex-col items-center gap-2 py-3 mx-2 my-auto bg-white border border-gray-200 rounded-lg shadow-sm">
          {[
            { icon: PlusCircle, title: "Add" },
            { icon: Clipboard, title: "Paste" },
            { icon: MousePointer2, title: "Select" },
            { icon: Hand, title: "Pan" },
            { icon: Grid3X3, title: "Snap to grid" },
            { icon: LayoutDashboard, title: "Minimap" },
            { icon: MoreHorizontal, title: "More options" },
          ].map(({ icon: Icon, title }, index) => (
            <button
              key={index}
              type="button"
              title={title}
              onClick={() => {
                setActiveCanvasTool(index);
                if (index === 0) addAgent();
              }}
              className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
                activeCanvasTool === index
                  ? "bg-indigo-600 text-white"
                  : "text-gray-500 hover:bg-indigo-50 hover:text-indigo-600"
              }`}
            >
              <Icon size={18} />
            </button>
          ))}
        </div>

        {/* Canvas area – canvas only (sub-header is full width above) */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          {/* Canvas – pan, connectors, draggable nodes; zoom floating bottom-left */}
          <div
            ref={canvasRef}
            className="flex-1 overflow-auto relative"
            style={{
              backgroundImage: "radial-gradient(#e5e7eb 1px, transparent 1px)",
              backgroundSize: "20px 20px",
              cursor: isPanning ? "grabbing" : "grab",
            }}
            onMouseDown={handleCanvasMouseDown}
          >
          {/* Floating zoom controls – always visible on canvas (ocr-ui style) */}
          <div
            className="absolute bottom-5 left-5 z-10 flex gap-1 bg-white rounded-lg border border-gray-200 shadow-md p-1.5"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleZoomOut}
              className="p-2 rounded-md hover:bg-gray-100 text-gray-600"
              title="Zoom out"
            >
              <ZoomOut size={18} />
            </button>
            <button
              onClick={handleZoomIn}
              className="p-2 rounded-md hover:bg-gray-100 text-gray-600"
              title="Zoom in"
            >
              <ZoomIn size={18} />
            </button>
            <button
              onClick={handleFit}
              className={`p-2 rounded-md hover:bg-gray-100 text-gray-600 ${isFittedView ? "bg-indigo-50 text-indigo-600" : ""}`}
              title={isFittedView ? "Restore normal view" : "Fit to screen"}
            >
              {isFittedView ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>
          </div>
          <div className="p-6 min-h-full">
          <div
            ref={contentRef}
            className="inline-block relative"
            style={{
              transform: `scale(${zoom}) translate(${pan.x}px, ${pan.y}px)`,
              transformOrigin: "0 0",
            }}
          >
            <svg className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-visible" style={{ zIndex: 1 }}>
              <defs>
                <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="#94a3b8" />
                </marker>
                <marker id="arrowhead-start" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                  <polygon points="10 0, 0 3.5, 10 7" fill="#94a3b8" />
                </marker>
                <marker id="arrowhead-completed" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="#34a853" />
                </marker>
                <marker id="arrowhead-start-completed" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                  <polygon points="10 0, 0 3.5, 10 7" fill="#34a853" />
                </marker>
              </defs>
              {svgLines.map((line) => (
                <path
                  key={line.id}
                  d={line.d}
                  fill="none"
                  stroke={line.stroke}
                  strokeWidth={line.strokeWidth}
                  markerEnd={line.markerEnd}
                  markerStart={line.markerStart}
                />
              ))}
            </svg>
            <div className="relative flex items-center gap-8" style={{ zIndex: 2 }}>
              {startNode && (
                <div
                  data-node-id={startNode.component_id}
                  className="shrink-0 cursor-grab active:cursor-grabbing"
                  style={{ transform: `translate(${nodePositions[startNode.component_id]?.x ?? 0}px, ${nodePositions[startNode.component_id]?.y ?? 0}px)` }}
                  onMouseDown={(e) => handleNodeMouseDown(e, startNode.component_id)}
                >
                  <WorkflowNodeCard node={startNode} getNodeIcon={getNodeIcon} getNodeIconBg={getNodeIconBg} getModelName={getModelName} isSelected={selectedNode?.component_id === startNode.component_id} onOpenEdit={handleNodeClick} />
                </div>
              )}
              {masterNode && (
                <div
                  data-node-id={masterNode.component_id}
                  className="shrink-0 cursor-grab active:cursor-grabbing"
                  style={{ transform: `translate(${nodePositions[masterNode.component_id]?.x ?? 0}px, ${nodePositions[masterNode.component_id]?.y ?? 0}px)` }}
                  onMouseDown={(e) => handleNodeMouseDown(e, masterNode.component_id)}
                >
                  <WorkflowNodeCard node={masterNode} getNodeIcon={getNodeIcon} getNodeIconBg={getNodeIconBg} getModelName={getModelName} isSelected={selectedNode?.component_id === masterNode.component_id} onOpenEdit={handleNodeClick} />
                </div>
              )}
              <div className="flex flex-col gap-6 shrink-0">
                {agentNodes.map((node) => (
                    <div
                      key={node.component_id}
                      data-node-id={node.component_id}
                      className="shrink-0 cursor-grab active:cursor-grabbing"
                      style={{ transform: `translate(${nodePositions[node.component_id]?.x ?? 0}px, ${nodePositions[node.component_id]?.y ?? 0}px)` }}
                      onMouseDown={(e) => handleNodeMouseDown(e, node.component_id)}
                    >
                      <WorkflowNodeCard node={node} getNodeIcon={getNodeIcon} getNodeIconBg={getNodeIconBg} getModelName={getModelName} isSelected={selectedNode?.component_id === node.component_id} onOpenEdit={handleNodeClick} />
                    </div>
                  ))}
              </div>
              {endNode && (
                <div
                  data-node-id={endNode.component_id}
                  className="shrink-0 cursor-grab active:cursor-grabbing"
                  style={{ transform: `translate(${nodePositions[endNode.component_id]?.x ?? 0}px, ${nodePositions[endNode.component_id]?.y ?? 0}px)` }}
                  onMouseDown={(e) => handleNodeMouseDown(e, endNode.component_id)}
                >
                  <WorkflowNodeCard node={endNode} getNodeIcon={getNodeIcon} getNodeIconBg={getNodeIconBg} getModelName={getModelName} isSelected={selectedNode?.component_id === endNode.component_id} onOpenEdit={handleNodeClick} />
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
        </div>
      </div>

      {/* Right panel – Add Agent or Edit Node (slides in from right) */}
      {(stagingNewAgent || selectedNode) &&
        createPortal(
          <div
            className={`fixed top-0 right-0 h-screen w-[400px] max-w-[90vw] bg-slate-50 shadow-2xl transition-all duration-500 ease-[cubic-bezier(0.25,0.8,0.25,1)] z-[9999] flex flex-col border-l border-gray-200
              ${rightPanelVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"}`}
          >
            {stagingNewAgent ? (
              <>
                <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-white">
                  <span className="text-base font-semibold text-gray-900">New Agent</span>
                  <button type="button" onClick={handleCloseRightPanel} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500" title="Close">
                    <X size={20} />
                  </button>
                </div>
                <div className="p-4 flex-1 overflow-auto space-y-4 bg-slate-50">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Name</label>
                    <input type="text" value={newAgentName} onChange={(e) => setNewAgentName(e.target.value)} placeholder="Agent name" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Input type</label>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setInputType("text")} className={`px-3 py-2 text-sm rounded-lg border ${inputType === "text" ? "bg-indigo-100 border-indigo-400 text-indigo-800" : "border-gray-200 text-gray-600 bg-white hover:bg-gray-50"}`}>Text</button>
                      <button type="button" onClick={() => setInputType("audio")} className={`px-3 py-2 text-sm rounded-lg border ${inputType === "audio" ? "bg-indigo-100 border-indigo-400 text-indigo-800" : "border-gray-200 text-gray-600 bg-white hover:bg-gray-50"}`}>Audio</button>
                    </div>
                  </div>
                  {inputType === "text" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Text input</label>
                      <textarea value={inputText} onChange={(e) => setInputText(e.target.value)} placeholder="Enter input..." rows={3} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none bg-white" />
                    </div>
                  )}
                  {inputType === "audio" && (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <button type="button" className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 bg-white"><Mic size={16} /> Record</button>
                        <button type="button" className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 bg-white"><Upload size={16} /> Upload</button>
                      </div>
                      <p className="text-xs text-gray-500">Audio record/upload (placeholder)</p>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Model</label>
                    <select value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white">
                      <option value="">Select model</option>
                      <option value="openai/gpt-oss-120b">openai/gpt-oss-120b</option>
                      <option value="llama-3">llama-3</option>
                    </select>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <button type="button" onClick={handleCloseRightPanel} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 bg-white">Cancel</button>
                    <button type="button" onClick={confirmAddAgent} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700">Confirm & Add</button>
                  </div>
                </div>
              </>
            ) : selectedNode ? (
              <>
                <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-white">
                  <span className="text-base font-semibold text-gray-900 capitalize">{selectedNode.component_type}</span>
                  <button type="button" onClick={handleCloseRightPanel} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500" title="Close">
                    <X size={20} />
                  </button>
                </div>
                <div className="p-4 flex-1 overflow-auto space-y-4 bg-slate-50">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Name</label>
                    <input
                      type="text"
                      value={selectedNode.component_name}
                      onChange={(e) => updateNode(selectedNode.component_id, { component_name: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                    />
                  </div>
                  {(selectedNode.input_type?.length ?? 0) > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Input type</label>
                      <div className="flex gap-2 flex-wrap">
                        {(selectedNode.input_type ?? []).map((type) => (
                          <span key={type} className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-700">{type}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedNode.component_type === "agent" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Model</label>
                      <select
                        value={selectedNode.models?.text?.[0] ?? ""}
                        onChange={(e) => updateNode(selectedNode.component_id, { models: { text: e.target.value ? [e.target.value] : [] } })}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                      >
                        <option value="">Select model</option>
                        <option value="openai/gpt-oss-120b">openai/gpt-oss-120b</option>
                        <option value="gpt-4o-mini">gpt-4o-mini</option>
                        <option value="llama-3">llama-3</option>
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Connects to</label>
                    <div className="flex flex-col gap-1.5 max-h-32 overflow-auto">
                      {nodes.filter((n) => n.component_id !== selectedNode.component_id).map((n) => {
                        const connected = selectedNode.next_component_id?.includes(n.component_id) ?? false;
                        return (
                          <label key={n.component_id} className={`flex items-center gap-2 text-sm py-1.5 px-2 rounded cursor-pointer ${connected ? "bg-indigo-50" : ""}`}>
                            <input
                              type="checkbox"
                              checked={connected}
                              onChange={(e) => {
                                const next = selectedNode.next_component_id ?? [];
                                const nextIds = e.target.checked ? [...next, n.component_id] : next.filter((id) => id !== n.component_id);
                                updateNode(selectedNode.component_id, { next_component_id: nextIds });
                              }}
                            />
                            {n.component_name} ({n.component_type})
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Connected from</label>
                    <div className="flex flex-col gap-1.5 max-h-32 overflow-auto">
                      {nodes.filter((n) => n.component_id !== selectedNode.component_id && (n.next_component_id?.includes(selectedNode.component_id) ?? false)).length === 0 ? (
                        <p className="text-xs text-gray-500">None</p>
                      ) : (
                        nodes.filter((n) => n.component_id !== selectedNode.component_id && (n.next_component_id?.includes(selectedNode.component_id) ?? false)).map((n) => (
                          <div key={n.component_id} className="text-sm py-1.5 px-2 rounded bg-gray-100">
                            {n.component_name} ({n.component_type})
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  {selectedNode.component_type === "agent" && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Agent info</label>
                        <textarea
                          value={selectedNode.agent_info ?? ""}
                          onChange={(e) => updateNode(selectedNode.component_id, { agent_info: e.target.value })}
                          placeholder="Optional description"
                          rows={2}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">System prompt</label>
                        <textarea
                          value={selectedNode.system_prompt ?? ""}
                          onChange={(e) => updateNode(selectedNode.component_id, { system_prompt: e.target.value })}
                          placeholder="Optional"
                          rows={2}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">User prompt</label>
                        <textarea
                          value={selectedNode.user_prompt ?? ""}
                          onChange={(e) => updateNode(selectedNode.component_id, { user_prompt: e.target.value })}
                          placeholder="Optional"
                          rows={2}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none bg-white"
                        />
                      </div>
                    </>
                  )}
                  <div className="flex gap-2 pt-2">
                    {!CORE_NODE_IDS.includes(selectedNode.component_id) && (
                      <button
                        type="button"
                        onClick={() => deleteNode(selectedNode.component_id)}
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100"
                      >
                        <Trash2 size={16} /> Delete
                      </button>
                    )}
                    <button type="button" className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100" title="Run workflow (placeholder)">
                      <PlayCircle size={16} /> Proceed
                    </button>
                  </div>
                </div>
              </>
            ) : null}
          </div>,
          document.body,
        )}
    </div>
  );
}
